import { useCallback, useEffect, useRef, useState } from 'react';

export interface SwipeToCloseState {
  /** 当前手势位移（向下为正）。 */
  offset: number;
  closing: boolean;
  onTouchStart: (event: React.TouchEvent) => void;
  onTouchMove: (event: React.TouchEvent) => void;
  onTouchEnd: () => void;
}

/**
 * 全屏面板下拉关闭：
 * - 只有内容滚动到顶部（scrollTop <= 0）时才开始响应，避免抢走内部滚动；
 * - 位移超过 120px 或速度超过 0.6px/ms 判定关闭；
 * - 同时处理 ESC 关闭与背景滚动锁定。
 */
export function useSwipeToClose(onClose: () => void, options: { scrollRef?: React.RefObject<HTMLElement | null>; distance?: number; velocity?: number; enabled?: boolean } = {}): SwipeToCloseState {
  const { scrollRef, distance: maxDistance = 120, velocity: maxVelocity = 0.6, enabled = true } = options;
  const [offset, setOffset] = useState(0);
  const [closing, setClosing] = useState(false);
  const startY = useRef<number | null>(null);
  const startTime = useRef(0);
  const offsetRef = useRef(0);

  // ESC 关闭 + 锁定背景滚动
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled, onClose]);

  const onTouchStart = useCallback((event: React.TouchEvent) => {
    if (!enabled) return;
    const scroller = scrollRef?.current;
    if (scroller && scroller.scrollTop > 0) { startY.current = null; return; }
    startY.current = event.touches[0]!.clientY;
    startTime.current = Date.now();
    offsetRef.current = 0;
  }, [enabled, scrollRef]);

  const onTouchMove = useCallback((event: React.TouchEvent) => {
    if (!enabled || startY.current === null) return;
    const delta = event.touches[0]!.clientY - startY.current;
    if (delta <= 0) { offsetRef.current = 0; setOffset(0); return; }
    offsetRef.current = delta;
    setOffset(delta);
  }, [enabled]);

  const onTouchEnd = useCallback(() => {
    if (!enabled || startY.current === null) return;
    const elapsed = Math.max(1, Date.now() - startTime.current);
    const speed = offsetRef.current / elapsed;
    const shouldClose = offsetRef.current >= maxDistance || (offsetRef.current > 40 && speed >= maxVelocity);
    startY.current = null;
    offsetRef.current = 0;
    setOffset(0);
    if (shouldClose) {
      setClosing(true);
      setTimeout(onClose, 180);
    }
  }, [enabled, maxDistance, maxVelocity, onClose]);

  return { offset, closing, onTouchStart, onTouchMove, onTouchEnd };
}
